/**
 * Ad Expense Comparison Report Renderer
 *
 * Vertical per-location layout showing new units by brand, used units,
 * advertising expense accounts, and cost per unit sold — with year-over-year variance.
 */

import React from 'react';

interface BrandUnits {
  brand: string;
  current: number;
  prior: number;
}

interface ExpenseAccount {
  account: string;
  description: string;
  current: number;
  prior: number;
}

interface LocationData {
  locationId: number;
  locationName: string;
  newUnitsByBrand: BrandUnits[];
  totalNewCurrent: number;
  totalNewPrior: number;
  usedUnitsCurrent: number;
  usedUnitsPrior: number;
  totalUnitsCurrent: number;
  totalUnitsPrior: number;
  expenses: ExpenseAccount[];
  totalExpenseCurrent: number;
  totalExpensePrior: number;
  costPerUnitCurrent: number | null;
  costPerUnitPrior: number | null;
}

export interface AdExpenseComparisonData {
  locations: LocationData[];
  grandTotals: {
    newUnitsByBrand: BrandUnits[];
    totalNewCurrent: number;
    totalNewPrior: number;
    usedUnitsCurrent: number;
    usedUnitsPrior: number;
    totalUnitsCurrent: number;
    totalUnitsPrior: number;
    expenses: ExpenseAccount[];
    totalExpenseCurrent: number;
    totalExpensePrior: number;
    costPerUnitCurrent: number | null;
    costPerUnitPrior: number | null;
  };
  year: number;
  month: number;
  monthName: string;
}

// Income accounts (008x) are credits that reduce net ad expense
const INCOME_ACCOUNTS = new Set(['008A', '008AK', '008B', '008FCP', '008V', '008W']);

const currFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const cpuFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmtCurrency(v: number): string {
  if (v < 0) return `(${currFmt.format(Math.abs(v))})`;
  return currFmt.format(v);
}

function fmtCPU(v: number | null): string {
  if (v === null) return '--';
  if (v < 0) return `(${cpuFmt.format(Math.abs(v))})`;
  return cpuFmt.format(v);
}

function fmtVariance(v: number): string {
  const sign = v > 0 ? '+' : '';
  return `${sign}${v}`;
}

function fmtCurrencyVariance(v: number): string {
  if (v > 0) return `+${currFmt.format(v)}`;
  if (v < 0) return `(${currFmt.format(Math.abs(v))})`;
  return currFmt.format(0);
}

function fmtCPUVariance(v: number | null): string {
  if (v === null) return '--';
  if (v > 0) return `+${cpuFmt.format(v)}`;
  if (v < 0) return `(${cpuFmt.format(Math.abs(v))})`;
  return cpuFmt.format(0);
}

function varColor(v: number, invert = false): string {
  const positive = invert ? v < 0 : v > 0;
  const negative = invert ? v > 0 : v < 0;
  if (positive) return 'text-green-600';
  if (negative) return 'text-red-600';
  return 'text-gray-500';
}

// For expenses, lower is better (invert colors)
function expVarColor(v: number): string {
  return varColor(v, true);
}

// For units, higher is better
function unitVarColor(v: number): string {
  return varColor(v);
}

// For cost per unit, lower is better (invert)
function cpuVarColor(v: number): string {
  return varColor(v, true);
}

interface LocationSectionProps {
  data: LocationData | AdExpenseComparisonData['grandTotals'] & { locationName: string };
  year: number;
  monthName: string;
  isGrandTotal?: boolean;
}

const LocationSection: React.FC<LocationSectionProps> = ({ data, year, monthName, isGrandTotal }) => {
  const priorYear = year - 1;
  const headerBg = isGrandTotal ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-900';
  const sectionBg = isGrandTotal ? 'bg-gray-50' : '';

  const incomeExpenses = data.expenses.filter((e) => INCOME_ACCOUNTS.has(e.account));
  const adExpenses = data.expenses.filter((e) => !INCOME_ACCOUNTS.has(e.account));

  return (
    <div className={`mb-6 ${sectionBg}`}>
      {/* Location header */}
      <div className={`${headerBg} px-4 py-2 rounded-t-md font-semibold text-sm`}>
        {data.locationName}
      </div>

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-200 text-xs text-gray-500">
            <th className="text-left py-2 px-4 w-1/2"></th>
            <th className="text-right py-2 px-2 w-1/6">{monthName} {year}</th>
            <th className="text-right py-2 px-2 w-1/6">{monthName} {priorYear}</th>
            <th className="text-right py-2 px-2 w-1/6">Variance</th>
          </tr>
        </thead>
        <tbody>
          {/* NEW UNITS section */}
          <tr className="bg-blue-50 border-b border-gray-200">
            <td colSpan={4} className="py-1.5 px-4 font-semibold text-blue-800 text-xs uppercase tracking-wide">
              New Units
            </td>
          </tr>
          {data.newUnitsByBrand.map((bu) => {
            const variance = bu.current - bu.prior;
            return (
              <tr key={bu.brand} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-1.5 px-4 pl-8 text-gray-700">{bu.brand}</td>
                <td className="py-1.5 px-2 text-right font-mono">{bu.current}</td>
                <td className="py-1.5 px-2 text-right font-mono text-gray-500">{bu.prior}</td>
                <td className={`py-1.5 px-2 text-right font-mono ${unitVarColor(variance)}`}>
                  {fmtVariance(variance)}
                </td>
              </tr>
            );
          })}
          {/* Total New */}
          <tr className="border-b border-gray-200 font-medium">
            <td className="py-1.5 px-4 pl-8 text-gray-900">Total New</td>
            <td className="py-1.5 px-2 text-right font-mono">{data.totalNewCurrent}</td>
            <td className="py-1.5 px-2 text-right font-mono text-gray-500">{data.totalNewPrior}</td>
            <td className={`py-1.5 px-2 text-right font-mono ${unitVarColor(data.totalNewCurrent - data.totalNewPrior)}`}>
              {fmtVariance(data.totalNewCurrent - data.totalNewPrior)}
            </td>
          </tr>

          {/* USED UNITS */}
          <tr className="border-b border-gray-200">
            <td className="py-1.5 px-4 font-medium text-gray-900">Used Units</td>
            <td className="py-1.5 px-2 text-right font-mono">{data.usedUnitsCurrent}</td>
            <td className="py-1.5 px-2 text-right font-mono text-gray-500">{data.usedUnitsPrior}</td>
            <td className={`py-1.5 px-2 text-right font-mono ${unitVarColor(data.usedUnitsCurrent - data.usedUnitsPrior)}`}>
              {fmtVariance(data.usedUnitsCurrent - data.usedUnitsPrior)}
            </td>
          </tr>

          {/* TOTAL UNITS */}
          <tr className="border-b-2 border-gray-300 font-bold bg-gray-50">
            <td className="py-2 px-4 text-gray-900">Total Units</td>
            <td className="py-2 px-2 text-right font-mono">{data.totalUnitsCurrent}</td>
            <td className="py-2 px-2 text-right font-mono text-gray-500">{data.totalUnitsPrior}</td>
            <td className={`py-2 px-2 text-right font-mono ${unitVarColor(data.totalUnitsCurrent - data.totalUnitsPrior)}`}>
              {fmtVariance(data.totalUnitsCurrent - data.totalUnitsPrior)}
            </td>
          </tr>

          {/* AD INCOME section (008x accounts) */}
          {incomeExpenses.length > 0 && (
            <>
              <tr className="bg-green-50 border-b border-gray-200">
                <td colSpan={4} className="py-1.5 px-4 font-semibold text-green-800 text-xs uppercase tracking-wide">
                  Ad Income / Allowances
                </td>
              </tr>
              {incomeExpenses.map((exp) => {
                const variance = exp.current - exp.prior;
                return (
                  <tr key={exp.account} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-1.5 px-4 pl-8 text-gray-700">
                      <span className="text-gray-400 text-xs mr-2">{exp.account}</span>
                      {exp.description}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono">{fmtCurrency(exp.current)}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-gray-500">{fmtCurrency(exp.prior)}</td>
                    <td className={`py-1.5 px-2 text-right font-mono ${expVarColor(variance)}`}>
                      {fmtCurrencyVariance(variance)}
                    </td>
                  </tr>
                );
              })}
            </>
          )}

          {/* AD EXPENSE section (019x accounts) */}
          {adExpenses.length > 0 && (
            <>
              <tr className="bg-red-50 border-b border-gray-200">
                <td colSpan={4} className="py-1.5 px-4 font-semibold text-red-800 text-xs uppercase tracking-wide">
                  Advertising Expenses
                </td>
              </tr>
              {adExpenses.map((exp) => {
                const variance = exp.current - exp.prior;
                return (
                  <tr key={exp.account} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-1.5 px-4 pl-8 text-gray-700">
                      <span className="text-gray-400 text-xs mr-2">{exp.account}</span>
                      {exp.description}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono">{fmtCurrency(exp.current)}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-gray-500">{fmtCurrency(exp.prior)}</td>
                    <td className={`py-1.5 px-2 text-right font-mono ${expVarColor(variance)}`}>
                      {fmtCurrencyVariance(variance)}
                    </td>
                  </tr>
                );
              })}
            </>
          )}

          {/* TOTAL AD EXPENSE */}
          <tr className="border-b-2 border-gray-300 font-bold bg-gray-50">
            <td className="py-2 px-4 text-gray-900">Total Net Ad Expense</td>
            <td className="py-2 px-2 text-right font-mono">{fmtCurrency(data.totalExpenseCurrent)}</td>
            <td className="py-2 px-2 text-right font-mono text-gray-500">{fmtCurrency(data.totalExpensePrior)}</td>
            <td className={`py-2 px-2 text-right font-mono ${expVarColor(data.totalExpenseCurrent - data.totalExpensePrior)}`}>
              {fmtCurrencyVariance(data.totalExpenseCurrent - data.totalExpensePrior)}
            </td>
          </tr>

          {/* COST PER UNIT */}
          <tr className="font-bold bg-pink-50">
            <td className="py-2 px-4 text-pink-900">Cost Per Unit</td>
            <td className="py-2 px-2 text-right font-mono">{fmtCPU(data.costPerUnitCurrent)}</td>
            <td className="py-2 px-2 text-right font-mono text-gray-500">{fmtCPU(data.costPerUnitPrior)}</td>
            <td className={`py-2 px-2 text-right font-mono ${
              data.costPerUnitCurrent !== null && data.costPerUnitPrior !== null
                ? cpuVarColor(data.costPerUnitCurrent - data.costPerUnitPrior)
                : 'text-gray-500'
            }`}>
              {fmtCPUVariance(
                data.costPerUnitCurrent !== null && data.costPerUnitPrior !== null
                  ? data.costPerUnitCurrent - data.costPerUnitPrior
                  : null
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

interface Props {
  data: AdExpenseComparisonData;
}

export const AdExpenseComparisonReport: React.FC<Props> = ({ data }) => {
  const { locations, grandTotals, year, monthName } = data;

  return (
    <div className="ad-expense-comparison-report">
      <style>{`
        @media print {
          @page { size: portrait; margin: 0.5in; }
          .ad-expense-comparison-report {
            width: 100% !important;
            overflow: visible !important;
          }
          .ad-expense-comparison-report table {
            font-size: 9pt !important;
          }
          .ad-expense-comparison-report td,
          .ad-expense-comparison-report th {
            padding: 2px 4px !important;
          }
        }
      `}</style>

      {/* Per-location sections */}
      {locations.map((loc) => (
        <LocationSection
          key={loc.locationId}
          data={loc}
          year={year}
          monthName={monthName}
        />
      ))}

      {/* Grand totals */}
      {locations.length > 1 && (
        <LocationSection
          data={{ ...grandTotals, locationName: 'ALL LOCATIONS — GRAND TOTAL' }}
          year={year}
          monthName={monthName}
          isGrandTotal
        />
      )}
    </div>
  );
};
